import { Button } from "@sellar-io/components";
import {
  Dialog,
  DialogNavigation,
  DialogContent,
} from "../../../../../../components/modal/Dialog";

import { ItemDiscountForm } from "../forms/ItemDiscountForm";
import {
  ItemDiscountType,
  useItemDiscountCardContext,
} from "../BusinessConnectionItemDiscountsCard.shared";
import { useSupplierRelationshipProductItemDiscountDelete } from "../../../../../../hooks/api/v2/supplierRelationshipProductItemDiscount/useSupplierRelationshipProductItemDiscountDelete";
import { useBuyerProduct } from "../../../../../../hooks/api/v2/product/useBuyerProduct";
import { useSupplierRelationshipProductItemDiscountUpdate } from "../../../../../../hooks/api/v2/supplierRelationshipProductItemDiscount/useSupplierRelationshipProductItemDiscountUpdate";
import { calculateBiggestContainerDiscountPrice } from "@sellar-io/common";
import { SpinnerLegacy } from "../../../../../../components";

const useEditItemDiscountDialog = ({
  productItemId,
  productItemDiscountId,
}: {
  productItemId: number;
  productItemDiscountId: number;
}) => {
  const { containerTypeDiscountList, connection } =
    useItemDiscountCardContext();

  const product = useBuyerProduct(productItemId, {
    RetailerId: connection.retailerId,
    enabled: productItemId !== undefined && productItemId > 1,
  });

  const productFullPrice = product.data?.ProductPriceTier?.fullPrice;
  const containerType = product.data?.containerType;

  const connectionItemDiscountDelete =
    useSupplierRelationshipProductItemDiscountDelete(productItemDiscountId);

  const connectionItemDiscountUpdate =
    useSupplierRelationshipProductItemDiscountUpdate(productItemDiscountId);

  const biggestContainerDiscountPrice =
    productFullPrice && containerType
      ? calculateBiggestContainerDiscountPrice({
          connectionDiscounts: containerTypeDiscountList,
          product: {
            containerType,
            productPriceTier: {
              price: productFullPrice,
            },
          },
        })
      : undefined;

  return {
    product: product.data ? product.data : undefined,
    connectionItemDiscountUpdate,
    connectionItemDiscountDelete,
    biggestContainerDiscountPrice,
  };
};

const EditItemDiscountDialogContent = ({
  itemDiscount,
  onUpdateSuccess,
  onDeleteSuccess,
}: {
  itemDiscount: ItemDiscountType;
  onUpdateSuccess: () => void;
  onDeleteSuccess: () => void;
}) => {
  const { setEditingItemDiscount } = useItemDiscountCardContext();

  const {
    product,
    connectionItemDiscountUpdate,
    connectionItemDiscountDelete,
    biggestContainerDiscountPrice,
  } = useEditItemDiscountDialog({
    productItemId: itemDiscount.ProductItemId,
    productItemDiscountId: itemDiscount.id,
  });

  const productItemFullPrice =
    typeof product?.ProductPriceTier?.fullPrice === "number"
      ? product.ProductPriceTier.fullPrice
      : undefined;

  return (
    <div>
      <DialogNavigation
        leftIcon={"CHEVRON_DOWN"}
        onClickLeftIcon={() => setEditingItemDiscount(undefined)}
        title={"Edit product item discount"}
      />
      <DialogContent>
        {productItemFullPrice && biggestContainerDiscountPrice && (
          <ItemDiscountForm
            itemPriceBeforeDiscount={productItemFullPrice}
            productName={product?.name ?? ""}
            itemDiscount={itemDiscount}
            containerTypeDiscountAmount={
              productItemFullPrice - biggestContainerDiscountPrice
            }
            onSubmit={(productItemDiscount) => {
              connectionItemDiscountUpdate.mutate(
                {
                  SupplierRetailerConnectionId:
                    itemDiscount.SupplierRetailerConnectionId,
                  ProductItemId: itemDiscount.ProductItemId,
                  discountAmount: productItemDiscount.discountAmount,
                  type: productItemDiscount.type,
                },
                {
                  onSuccess: () => onUpdateSuccess(),
                },
              );
            }}
          >
            <div className={"flex w-full gap-2 *:grow"}>
              <Button
                color={"red"}
                onClick={() =>
                  connectionItemDiscountDelete.mutate(undefined, {
                    onSuccess: () => onDeleteSuccess(),
                  })
                }
              >
                Remove
              </Button>
              <Button type={"submit"}>Update</Button>
            </div>
          </ItemDiscountForm>
        )}
      </DialogContent>
    </div>
  );
};

export const EditItemDiscountDialog = ({
  active,
  onDismiss,
  editingItemDiscount,
}: {
  active: boolean;
  onDismiss: () => void;
  editingItemDiscount?: ItemDiscountType;
}) => {
  return (
    <Dialog active={active} onDismiss={onDismiss}>
      {!!editingItemDiscount ? (
        <EditItemDiscountDialogContent
          itemDiscount={editingItemDiscount}
          onUpdateSuccess={onDismiss}
          onDeleteSuccess={onDismiss}
        />
      ) : (
        <SpinnerLegacy />
      )}
    </Dialog>
  );
};
